import type { Appearance } from '@stripe/stripe-js';

/**
 * Stripe Appearance API tokens for the payment page (funnel v2, ESCLOUD-332).
 * Stripe's iframe cannot read our CSS variables, so the theme tokens are
 * restated here, as hex, because Stripe validates appearance colours. The only
 * file in the client allowed to carry raw colours.
 *
 * Light column = the app's light theme, dark column = its dark theme; both from
 * the design token sheet, converted from oklch.
 */
export type TStripeTheme = 'light' | 'dark';

interface AppearanceTokens {
    colorText: string;
    colorTextSecondary: string;
    colorTextPlaceholder: string;
    colorBackground: string;
    colorPrimary: string;
    colorDanger: string;
    inputBorder: string;
    focusRing: string;
}

const TOKENS: Record<TStripeTheme, AppearanceTokens> = {
    light: {
        colorText: '#313e38', // oklch(0.35 0.02 165)
        colorTextSecondary: '#617581', // oklch(0.55 0.03 235)
        colorTextPlaceholder: '#788993', // oklch(0.62 0.025 235)
        colorBackground: '#ffffff',
        colorPrimary: '#008d9b', // oklch(0.55 0.18 200)
        colorDanger: '#b32228', // oklch(0.50 0.18 25)
        inputBorder: '#ced9e0', // oklch(0.88 0.015 235)
        focusRing: '#c2f3f5', // oklch(0.93 0.05 200)
    },
    dark: {
        colorText: '#e1ebe6', // oklch(0.93 0.012 165)
        colorTextSecondary: '#90a1ac', // oklch(0.70 0.025 235)
        colorTextPlaceholder: '#7b8891', // oklch(0.62 0.020 235)
        colorBackground: '#11171a', // oklch(0.20 0.012 230)
        colorPrimary: '#00afb8', // oklch(0.68 0.13 200)
        colorDanger: '#ed756e', // oklch(0.70 0.15 25)
        inputBorder: '#333f46', // oklch(0.36 0.02 235)
        focusRing: '#003c3f', // oklch(0.32 0.06 200)
    },
};

/** Lato for the iframe. fonts.googleapis.com is already in the SPA's CSP. */
export const STRIPE_FONTS = [
    { cssSrc: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap' },
];

export function stripeAppearance(theme: TStripeTheme): Appearance {
    const t = TOKENS[theme];
    return {
        theme: 'flat',
        variables: {
            fontFamily: 'Lato, Helvetica, sans-serif',
            fontSizeBase: '14px',
            colorText: t.colorText,
            colorTextSecondary: t.colorTextSecondary,
            colorTextPlaceholder: t.colorTextPlaceholder,
            colorBackground: t.colorBackground,
            colorPrimary: t.colorPrimary,
            colorDanger: t.colorDanger,
            borderRadius: '4px',
            spacingUnit: '4px',
            spacingGridRow: '10px',
        },
        rules: {
            '.Input': {
                border: `1px solid ${t.inputBorder}`,
                boxShadow: 'none',
                padding: '0 12px',
                lineHeight: '36px', // 38px tall with the border, per the token sheet
            },
            '.Input:focus': {
                borderColor: t.colorPrimary,
                boxShadow: `0 0 0 2px ${t.focusRing}`,
            },
            '.Input--invalid': {
                borderColor: t.colorDanger,
            },
            '.Label': {
                fontSize: '13px',
                fontWeight: '400',
                color: t.colorText,
                marginBottom: '5px',
            },
        },
    };
}
